import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { ConfigurationService } from './configuration-service';

@Injectable({ providedIn: 'root' })
export class TranslationService {
  http = inject(HttpClient);
  configService = inject(ConfigurationService);
  router = inject(Router);
  private route = inject(ActivatedRoute);

  private translations = signal<Record<string, any>>({});
  private currentModule = new BehaviorSubject<string>('');

  constructor() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const moduleCode = this.getModuleCodeFromRoute();
      if (moduleCode) this.currentModule.next(moduleCode);
    });
  }

  isTranslationsEmpty = computed(() => Object.keys(this.translations()).length === 0);

  private getModuleCodeFromRoute(): string {
    let activeRoute = this.route.firstChild;
    while (activeRoute?.firstChild) activeRoute = activeRoute.firstChild;
    return activeRoute?.snapshot.data?.['moduleCode'] || '';
  }

  setLocalStorage(langId: number) {
    localStorage.setItem('langId', langId.toString());
  }

  getLocalStorage() {
    const langIdLocalStorage = localStorage.getItem('langId') ?? 1;
    return Number(langIdLocalStorage);
  }

  async init(langId: number, isLoggedIn: boolean = true) {
    //isLoggedIn param is to determine whether to use langId from localStorage or not, as for login/register page, user is not logged in yet but we still want to load the translations based on selected language in dropdown which is stored in localStorage
    if (!this.isTranslationsEmpty() && langId === this.getLocalStorage()) {
      return;
    }

    if (!isLoggedIn) {
      langId = this.getLocalStorage();
    }

    try {
      const response = await firstValueFrom(this.configService.GetLanguagePackage(langId));

      if (response && (response as any).modules) {
        const data = (response as any).modules;
        this.translations.set(data[0].labels);
        this.setLocalStorage((response as any).language.id);
      }
    } catch (error) {
      this.translations.set({
        SYSTEM_UNDER_MAINTENANCE: 'System Under Maintenance',
        SYSTEM_UNDER_MAINTENANCE_DESC: 'We will be back shortly.',
        BACK_TO_HOME: 'Back to Home'
      });
    }
  }

  translate(key: string, params: Record<string, any> = {}, className: string | null = null): string {
    if (key === null || key === undefined) return '';

    if (!this.translations() || this.isTranslationsEmpty()) {
      return key;
    }

    if (typeof key !== 'string') return key;

    // not using module-specific translations for now, but can be implemented if needed
    // const module = this.currentModule.value;
    // const moduleDict = this.translations()[module] || {};
    // const globalDict = this.translations()[''] || {};
    // let text = moduleDict[key] || globalDict[key] || key;

    let text = this.translations()[key] || key;

    // Replace {0}, {1}, etc.
    text = text.replace(/{(\d+)}/g, (_: any, index: any) => params[index] ?? `{${index}}`);

    // Replace {{ param }}
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(new RegExp(`{{\\s*${k}\\s*}}`, 'g'), String(v));
    });

    if (className) {
      return `<span class="${className}">${text}</span>`;
    }

    return text;
  }

  formatListMany(list: string[]) {
    let formatted;
    let translatedText;

    if (list.length > 1) {
      formatted = {
        list0: list
          .slice(0, -1)
          .map((x) => `<b>${x}</b>`)
          .join(', '),
        list1: `<b>${list[list.length - 1]}</b>`
      };
      translatedText = this.translate('LIST_MANY', { '0': formatted.list0, '1': formatted.list1 });
    } else {
      translatedText = `<b>${list[0]}</b>`;
    }

    return translatedText;
  }

  translateCeption(mainKey: string, nestedKeys: string[]) {
    const translatedNestedKeys: string[] = [];

    nestedKeys.forEach((key) => {
      translatedNestedKeys.push(this.translate(key));
    });

    return this.translate(mainKey, translatedNestedKeys);
  }
}
